// src/pages/Warehouse/Snapshot/WarehouseSnapshotView.jsx
import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Table,
  DatePicker,
  Space,
  Typography,
  Button,
  Input,
  Spin,
  Divider,
  Row,
  Col,
  Tag,
  Tooltip,
  Select,
  Checkbox,
  Popover,
  Timeline,
} from "antd"
import {
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons"
import { collection, query, where, orderBy, getDocs, Timestamp } from "firebase/firestore"
import dayjs from "dayjs"
import { db } from "../../../../firebase/config"

const { Text } = Typography
const { RangePicker } = DatePicker

const nf = new Intl.NumberFormat("en-US")
const cf = new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" })

export default function WarehouseSnapshotView() {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState([])
  const [snapshot, setSnapshot] = useState([])

  const [dateRange, setDateRange] = useState([null, dayjs().endOf("day")])
  const [search, setSearch] = useState("")
  const [selectedLocation, setSelectedLocation] = useState("Warehouse")

  const [searchText, setSearchText] = useState("")
  const [productFilterActive, setProductFilterActive] = useState(false)
  const searchInputRef = useRef(null)

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    productName: true,
    quantity: true,
    pricePerUnit: true,
    totalPrice: true,
    productType: true,
    location: false,
    material: false,
    unit: false,
    width: false,
    batch: false,
    barcode: false,
    events: false,
  })

  const LOCATIONS = [
    { label: "Warehouse", value: "Warehouse" },
    { label: "Arsoma", value: "Arsoma" },
    { label: "Viva", value: "Viva" },
    { label: "Winder", value: "Winder" },
    { label: "Tape", value: "Tape" },
  ]

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true)
      try {
        const constraints = [orderBy("timestamp", "asc")]

        if (dateRange?.[0]) {
          const start = Timestamp.fromDate(dateRange[0].startOf("day").toDate())
          constraints.push(where("timestamp", ">=", start))
        }

        if (dateRange?.[1]) {
          const end = Timestamp.fromDate(dateRange[1].endOf("day").toDate())
          constraints.push(where("timestamp", "<=", end))
        }

        const q = query(collection(db, "warehouseEvents"), ...constraints)
        const snap = await getDocs(q)

        const eventData = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))

        setEvents(eventData)
      } catch (err) {
        console.error("Failed to fetch events:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [dateRange])

  useEffect(() => {
    const inventoryMap = new Map()

    events.forEach((event) => {
      const key = [
        event.productTypeName || "",
        event.productName || "",
        event.location || "",
        event.materialName || "",
        event.rollWidth || "",
        event.unit || "",
        event.barcode || "",
        event.ean || "",
      ].join("|")

      if (!inventoryMap.has(key)) {
        inventoryMap.set(key, {
          productTypeId: event.productTypeId,
          productTypeName: event.productTypeName,
          productName: event.productName,
          location: event.location,
          materialName: event.materialName,
          materialId: event.materialId,
          unit: event.unit,
          quantity: 0,
          rollWidth: event.rollWidth,
          barcode: event.barcode,
          ean: event.ean,
          batchSeries: event.batchSeries,
          pricePerUnit: null,
          totalPrice: 0,
          events: [],
        })
      }

      const item = inventoryMap.get(key)
      item.quantity += Number(event.quantityChange) || 0
      item.events.push(event)

      if (event.price) item.pricePerUnit = event.price
      if (event.batchSeries) item.batchSeries = event.batchSeries
      if (event.total) item.totalPrice += Number(event.total)
    })

    const snapshotData = Array.from(inventoryMap.values())
      .filter((item) => item.quantity > 0)
      .map((item, idx) => ({
        ...item,
        key: `snapshot-${idx}`,
      }))

    setSnapshot(snapshotData)
  }, [events])

  const searchedRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase()
    let filtered = snapshot

    if (selectedLocation) {
      filtered = filtered.filter((row) => (row.location || "") === selectedLocation)
    }

    if (!q) return filtered
    return filtered.filter((row) => {
      const hay = [
        row.productTypeName,
        row.productName,
        row.location,
        row.materialName,
        row.barcode,
        row.ean,
        row.batchSeries,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return hay.includes(q)
    })
  }, [snapshot, search, selectedLocation])

  const uniq = (arr) =>
    Array.from(new Set(arr.filter((x) => x !== undefined && x !== null && x !== "")))

  const productTypeFilters = useMemo(
    () => uniq(searchedRows.map((r) => r.productTypeName)).map((v) => ({ text: v, value: v })),
    [searchedRows]
  )

  const locationFilters = useMemo(
    () => uniq(searchedRows.map((r) => r.location)).map((v) => ({ text: v, value: v })),
    [searchedRows]
  )

  const materialFilters = useMemo(
    () => uniq(searchedRows.map((r) => r.materialName)).map((v) => ({ text: v, value: v })),
    [searchedRows]
  )

  const unitFilters = useMemo(
    () => uniq(searchedRows.map((r) => r.unit)).map((v) => ({ text: v, value: v })),
    [searchedRows]
  )

  const widthFilters = useMemo(
    () =>
      uniq(
        searchedRows.map((r) =>
          Number.isFinite(Number(r.rollWidth)) && Number(r.rollWidth) > 0
            ? Number(r.rollWidth)
            : null
        )
      )
        .filter((n) => n !== null)
        .sort((a, b) => a - b)
        .map((n) => ({ text: `${n}`, value: n })),
    [searchedRows]
  )

  const productBarcodeFilter = {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder="Search product / barcode"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm()
            setSearchText(selectedKeys[0] || "")
            setProductFilterActive(!!(selectedKeys[0] || ""))
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm()
              setSearchText(selectedKeys[0] || "")
              setProductFilterActive(!!(selectedKeys[0] || ""))
            }}>
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.()
              setSearchText("")
              setProductFilterActive(false)
              confirm({ closeDropdown: true })
            }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered || productFilterActive ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const q = String(value || "").toLowerCase()
      const hay = `${record.productName || ""} ${record.barcode || ""}`.toLowerCase()
      return hay.includes(q)
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInputRef.current?.select(), 100)
    },
  }

  // Expandable row render
  const expandedRowRender = (record) => {
    const sortedEvents = [...record.events].sort((a, b) => {
      const timeA = a.timestamp?.seconds || 0
      const timeB = b.timestamp?.seconds || 0
      return timeB - timeA // descending
    })

    return (
      <div style={{ padding: "12px 24px" }}>
        <Text strong>Event History ({sortedEvents.length} events)</Text>
        <Timeline style={{ marginTop: 16 }}>
          {sortedEvents.map((event, idx) => {
            const isPositive = (event.quantityChange || 0) > 0
            const timestamp = event.timestamp?.seconds
              ? dayjs.unix(event.timestamp.seconds).format("DD.MM.YYYY HH:mm")
              : "—"

            return (
              <Timeline.Item
                key={idx}
                color={isPositive ? "green" : "red"}
                dot={<ClockCircleOutlined style={{ fontSize: 16 }} />}>
                <Space direction="vertical" size={0}>
                  <Text strong>{event.eventType.replace(/_/g, " ").toUpperCase()}</Text>
                  <Text>
                    {isPositive ? "+" : ""}
                    {event.quantityChange} {event.unit || ""}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {timestamp}
                  </Text>
                  {event.metadata?.notes && (
                    <Text type="secondary" italic style={{ fontSize: 12 }}>
                      {event.metadata.notes}
                    </Text>
                  )}
                </Space>
              </Timeline.Item>
            )
          })}
        </Timeline>
      </div>
    )
  }

  // All column definitions
  const allColumns = [
    {
      key: "productType",
      title: "Product type",
      dataIndex: "productTypeName",
      width: 180,
      filters: productTypeFilters,
      onFilter: (value, record) => record.productTypeName === value,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      key: "location",
      title: "Location",
      dataIndex: "location",
      width: 140,
      filters: locationFilters,
      onFilter: (value, record) => record.location === value,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      key: "productName",
      title: "Product / Barcode",
      dataIndex: "productName",
      width: 280,
      ellipsis: true,
      render: (v, record) => (
        <Space direction="vertical" size={0}>
          <Text>{v || <Text type="secondary">—</Text>}</Text>
          {record.barcode && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.barcode}
            </Text>
          )}
        </Space>
      ),
      ...productBarcodeFilter,
    },
    {
      key: "material",
      title: "Material",
      dataIndex: "materialName",
      width: 200,
      filters: materialFilters,
      onFilter: (value, record) => record.materialName === value,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      key: "unit",
      title: "Unit",
      dataIndex: "unit",
      width: 90,
      align: "center",
      filters: unitFilters,
      onFilter: (value, record) => record.unit === value,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      key: "quantity",
      title: "Quantity",
      dataIndex: "quantity",
      width: 140,
      align: "right",
      sorter: (a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
      render: (v) =>
        Number.isFinite(Number(v)) ? nf.format(Number(v)) : <Text type="secondary">—</Text>,
    },
    {
      key: "pricePerUnit",
      title: "Price/unit",
      dataIndex: "pricePerUnit",
      width: 120,
      align: "right",
      sorter: (a, b) => (Number(a.pricePerUnit) || 0) - (Number(b.pricePerUnit) || 0),
      render: (v) =>
        Number.isFinite(Number(v)) ? cf.format(Number(v)) : <Text type="secondary">—</Text>,
    },
    {
      key: "totalPrice",
      title: "Total price",
      dataIndex: "totalPrice",
      width: 140,
      align: "right",
      sorter: (a, b) => (Number(a.totalPrice) || 0) - (Number(b.totalPrice) || 0),
      render: (v) =>
        Number.isFinite(Number(v)) ? cf.format(Number(v)) : <Text type="secondary">—</Text>,
    },
    {
      key: "width",
      title: "Width (mm)",
      dataIndex: "rollWidth",
      width: 120,
      align: "right",
      filters: widthFilters,
      onFilter: (value, record) => Number(record.rollWidth) === Number(value),
      sorter: (a, b) => (Number(a.rollWidth) || 0) - (Number(b.rollWidth) || 0),
      render: (v) =>
        Number.isFinite(Number(v)) && Number(v) > 0 ? (
          nf.format(Number(v))
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      key: "batch",
      title: "Batch/Series",
      dataIndex: "batchSeries",
      width: 140,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      key: "barcode",
      title: "Barcode/EAN",
      dataIndex: "barcode",
      width: 160,
      render: (v, record) => v || record.ean || <Text type="secondary">—</Text>,
    },
    {
      key: "events",
      title: "Events",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title={`Click row to see ${record.events.length} event(s)`}>
          <Tag color="blue">{record.events.length}</Tag>
        </Tooltip>
      ),
    },
  ]

  // Filter visible columns
  const columns = allColumns.filter((col) => visibleColumns[col.key])

  // Column selector popover
  const columnSelectorContent = (
    <div style={{ width: 250 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {allColumns.map((col) => (
          <Checkbox
            key={col.key}
            checked={visibleColumns[col.key]}
            onChange={(e) =>
              setVisibleColumns((prev) => ({ ...prev, [col.key]: e.target.checked }))
            }>
            {col.title}
          </Checkbox>
        ))}
      </Space>
    </div>
  )

  const resetFilters = () => {
    setDateRange([null, dayjs().endOf("day")])
    setSearch("")
    setSearchText("")
    setProductFilterActive(false)
    setSelectedLocation("null")
  }

  // Calculate totals for visible filtered rows
  const totals = useMemo(() => {
    return searchedRows.reduce(
      (acc, row) => {
        acc.quantity += Number(row.quantity) || 0
        acc.totalPrice += Number(row.totalPrice) || 0
        return acc
      },
      { quantity: 0, totalPrice: 0 }
    )
  }, [searchedRows])

  return (
    <div style={{ padding: 20 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Space size="middle" wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space size="middle" wrap>
              <Space direction="vertical" size={2}>
                <Text strong>Location</Text>
                <Select
                  style={{ width: 200 }}
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  options={LOCATIONS}
                />
              </Space>

              <Space direction="vertical" size={2}>
                <Text strong>Date Range</Text>
                <RangePicker
                  allowClear
                  value={dateRange}
                  onChange={(val) => setDateRange(val)}
                  format="DD.MM.YYYY"
                  placeholder={["From (any)", "To (today)"]}
                  style={{ width: 300 }}
                />
              </Space>

              <Space direction="vertical" size={2}>
                <Text strong>Search</Text>
                <Input
                  allowClear
                  style={{ width: 280 }}
                  prefix={<SearchOutlined />}
                  placeholder="Search all fields..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Space>
            </Space>

            <Space>
              <Popover content={columnSelectorContent} title="Show/Hide Columns" trigger="click">
                <Button icon={<SettingOutlined />}>Columns</Button>
              </Popover>
              <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                Reset
              </Button>
            </Space>
          </Space>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "12px 0" }} />
          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin size="large" tip="Loading events..." />
            </div>
          ) : (
            <Table
              rowKey="key"
              dataSource={searchedRows}
              columns={columns}
              bordered
              size="small"
              pagination={{ pageSize: 100, showSizeChanger: true }}
              expandable={{
                expandedRowRender,
                expandRowByClick: true,
              }}
              title={() => (
                <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                  <Space size="large">
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Items
                      </Text>
                      <Text strong style={{ fontSize: 18 }}>
                        {searchedRows.length}
                      </Text>
                    </Space>
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Quantity
                      </Text>
                      <Text strong style={{ fontSize: 18 }}>
                        {nf.format(totals.quantity)}
                      </Text>
                    </Space>
                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Value
                      </Text>
                      <Text strong style={{ fontSize: 18 }}>
                        {cf.format(totals.totalPrice)}
                      </Text>
                    </Space>
                  </Space>
                </div>
              )}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}
